<div class="w-60 2xl:w-96 flex flex-col border-r border-neutral-3 bg-white dark:bg-neutral-2 h-full">
  <!-- Header -->
  <div class="p-5 flex flex-col gap-4 border-b border-neutral-3">
    <div class="flex justify-between items-center">
      <h1 class="text-2xl font-bold tracking-tight">{{ title() }}</h1>
      <button mat-icon-button class="text-neutral-11 hover:bg-neutral-3">
        <mat-icon>tune</mat-icon>
      </button>
    </div>

    <!-- Search Bar -->
    <div class="relative flex items-center">
      <mat-icon class="absolute left-3 text-neutral-10 size-5 text-lg">search</mat-icon>
      <input
        type="text"
        [ngModel]="searchQuery()"
        (ngModelChange)="searchQuery.set($event)"
        [placeholder]="searchPlaceholder()"
        [attr.aria-label]="searchPlaceholder()"
        class="w-full rounded-2xl border border-neutral-3 bg-neutral-1 py-2.5 pl-10 pr-4 text-sm text-neutral-12 placeholder-neutral-10 focus:border-primary focus:outline-none transition-colors"
      />
    </div>

    <!-- Tabs -->
    <div class="flex gap-2">
      <!-- Tout / Archivées Split Button -->
      <div
        class="flex items-center rounded-full transition-all overflow-hidden"
        [class]="
          filterTab() === 'all' || filterTab() === 'archived'
            ? 'bg-neutral-12 text-white dark:text-neutral-2'
            : 'bg-neutral-2 dark:bg-neutral-3/20 text-neutral-11 hover:bg-neutral-3'
        "
      >
        <button
          (click)="selectTab(filterTab() === 'archived' ? 'archived' : 'all')"
          class="pl-4 pr-1.5 py-1.5 text-xs font-semibold transition-all cursor-pointer rounded-l-full hover:bg-black/5 dark:hover:bg-white/5"
        >
          {{ filterTab() === 'archived' ? 'Archivées' : 'Tout' }}
        </button>
        <button
          [matMenuTriggerFor]="allMenu"
          class="pr-3 pl-1.5 py-1.5 text-xs transition-all cursor-pointer rounded-r-full border-l border-neutral-3/20 flex items-center justify-center hover:bg-black/5 dark:hover:bg-white/5"
        >
          <mat-icon class="size-3.5 text-[14px] flex items-center justify-center"
            >keyboard_arrow_down</mat-icon
          >
        </button>
      </div>

      <mat-menu #allMenu="matMenu" class="rounded-2xl border border-neutral-3 shadow-lg">
        <button mat-menu-item (click)="selectTab('all')">
          <mat-icon class="text-neutral-11">chat_bubble_outline</mat-icon>
          <span>Toutes les discussions</span>
        </button>
        <button mat-menu-item (click)="selectTab('archived')">
          <mat-icon class="text-neutral-11">archive</mat-icon>
          <span>Discussions archivées</span>
        </button>
      </mat-menu>

      <button
        (click)="selectTab('unread')"
        [class]="
          filterTab() === 'unread'
            ? 'bg-neutral-12 text-white dark:text-neutral-2'
            : 'bg-neutral-2 dark:bg-neutral-3/20 text-neutral-11 hover:bg-neutral-3'
        "
        class="rounded-full px-4 py-1.5 text-xs font-semibold transition-all cursor-pointer"
      >
        Non lus
      </button>
    </div>
  </div>

  <!-- Thread List -->
  <div (scroll)="onScroll($event)" class="flex-1 overflow-y-auto divide-y divide-neutral-2">
    @if (isLoading()) {
      <div class="flex justify-center items-center h-48">
        <mat-spinner diameter="32"></mat-spinner>
      </div>
    } @else if (filteredDiscussions().length === 0) {
      <div class="flex flex-col items-center justify-center p-8 text-center text-neutral-10 h-48">
        <mat-icon class="size-10 text-4xl mb-2">chat_bubble_outline</mat-icon>
        <span class="text-sm font-semibold">{{ emptyText() }}</span>
      </div>
    } @else {
      @for (disc of filteredDiscussions(); track disc._id) {
        <div
          (click)="discussionSelected.emit(disc)"
          (keydown.enter)="discussionSelected.emit(disc)"
          (keydown.space)="discussionSelected.emit(disc); $event.preventDefault()"
          tabindex="0"
          role="button"
          [class.bg-neutral-2]="activeDiscussion()?._id === disc._id"
          class="p-4 flex gap-3 hover:bg-neutral-2/50 focus:bg-neutral-2/50 focus:outline-none cursor-pointer transition-colors relative group"
        >
          <!-- Avatar -->
          <div class="relative shrink-0">
            <div
              class="size-12 rounded-full bg-gradient-to-br from-primary-9 to-indigo-9 text-white flex items-center justify-center font-bold text-lg shadow-sm"
            >
              {{ getParticipantInitial(disc) }}
            </div>
            @if (avatarBadgeTemplate()) {
              <ng-container
                *ngTemplateOutlet="avatarBadgeTemplate()!; context: { $implicit: disc }"
              ></ng-container>
            }
          </div>

          <!-- Content Preview -->
          <div class="flex-1 min-w-0">
            <div class="flex justify-between items-baseline mb-1">
              <h3 class="text-sm font-bold truncate text-neutral-12 flex items-center gap-1.5 pr-6">
                {{ getParticipantName(disc) }}
                @if (isDiscussionStarred(disc)) {
                  <mat-icon class="text-amber-10 size-3.5 text-md flex items-center justify-center"
                    >star</mat-icon
                  >
                }
              </h3>
              <span class="text-[10px] text-neutral-10 font-semibold whitespace-nowrap">
                @if (disc.lastMessageAt) {
                  {{ disc.lastMessageAt | date: timeFormat() }}
                }
              </span>
            </div>

            <div class="flex justify-between items-center">
              <p class="text-xs text-neutral-10 truncate leading-relaxed flex-1 mr-2">
                {{ disc.lastMessageText || 'Aucun message dans cette discussion.' }}
              </p>
              @if (getUnreadCount(disc) > 0) {
                <span
                  class="flex size-5 shrink-0 items-center justify-center rounded-full bg-green-9 text-[9px] font-bold text-white shadow-sm"
                >
                  {{ getUnreadCount(disc) }}
                </span>
              }
            </div>
          </div>

          <!-- Action Menu trigger button -->
          <button
            mat-icon-button
            [matMenuTriggerFor]="menu"
            (click)="$event.stopPropagation()"
            class="absolute top-2 right-2 size-8 flex items-center justify-center rounded-full bg-white/80 dark:bg-neutral-2/80 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity shadow-sm z-10 border border-neutral-3"
          >
            <mat-icon class="text-neutral-11 size-4 text-base">more_horiz</mat-icon>
          </button>

          <!-- Mat Menu -->
          <mat-menu #menu="matMenu" xPosition="before" class="rounded-2xl border border-neutral-3 shadow-lg">
            <button mat-menu-item (click)="markAsUnread.emit({ discussion: disc, event: $event })">
              <mat-icon class="text-neutral-11">chat_bubble</mat-icon>
              <span>Marquer comme non lu</span>
            </button>
            <button mat-menu-item (click)="toggleStar.emit({ discussion: disc, event: $event })">
              <mat-icon class="text-neutral-11">
                {{ isDiscussionStarred(disc) ? 'star' : 'star_border' }}
              </mat-icon>
              <span>
                {{ isDiscussionStarred(disc) ? 'Retirer des importants' : 'Ajouter aux messages importants' }}
              </span>
            </button>
            <button mat-menu-item (click)="archiveDiscussion.emit({ discussion: disc, event: $event })">
              <mat-icon class="text-neutral-11">
                {{ isDiscussionArchived(disc) ? 'unarchive' : 'archive' }}
              </mat-icon>
              <span>{{ isDiscussionArchived(disc) ? 'Désarchiver' : 'Archiver' }}</span>
            </button>
          </mat-menu>
        </div>
      }
    }
  </div>
</div>
